import { PaymentProcessorType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/payment_processor_type_pb'
import { GetStoreConfigPublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/store_config/v1/store_config_pb'
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {
  StripeElementsOptionsMode,
  StripeError,
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

// import { toast } from 'sonner'
import { PaymentDetailsFormSchema } from '@/components/checkout/types'
import { StripeErrorAlert } from '@/components/payment/stripe/stripe-error-alert'

export type StripeExpressCheckoutProps = {
  storeConfigData?: GetStoreConfigPublicResponse
  siteSlug: string
  options: StripeElementsOptionsMode
  getPaymentParams: () => {
    clientSecret: string | undefined
    returnUrl: string
  }
  onPaymentInitiated?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
  onPaymentUpdated?: () => Promise<void>
  onPaymentConfirmed: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
}

export const StripeExpressCheckout = ({
  onPaymentInitiated,
  onPaymentConfirmed,
  getPaymentParams,
}: StripeExpressCheckoutProps) => {
  const { t } = useTranslation()

  const [stripeError, setStripeError] = useState<StripeError | undefined>(
    undefined,
  )
  const stripe = useStripe()
  const elements = useElements()

  const [visibility, setVisibility] = useState<
    'initial' | 'hidden' | 'visible'
  >('hidden')

  const onReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    if (!event.availablePaymentMethods) {
      // No buttons will show
    } else {
      setVisibility('initial')
    }
  }

  const handleError = (error: StripeError) => {
    setStripeError(error)
  }

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    const { clientSecret, returnUrl } = getPaymentParams()

    if (!stripe || !elements) {
      return
    }

    const input: PaymentDetailsFormSchema = {
      paymentMethod: {
        stripePaymentMethodId: '',
        stripePaymentIntentId: '',
        paymentProcessorType: PaymentProcessorType.STRIPE,
      },
      contact: {
        email: event?.billingDetails?.email || '',
        emailOptin: true,
      },
    }

    if (event?.shippingAddress) {
      const addressDetails = {
        name: event?.shippingAddress?.name,
        address1: event?.shippingAddress?.address?.line1,
        address2: event?.shippingAddress?.address?.line2 || '',
        city: event?.shippingAddress?.address?.city,
        state: event?.shippingAddress?.address?.state,
        postalCode: event?.shippingAddress?.address?.postal_code,
        country: event?.shippingAddress?.address?.country,
      }

      input.shipping = addressDetails
      input.shippingRateId = event?.shippingRate?.id
    }

    if (event?.billingDetails) {
      const addressDetails = {
        name: event?.billingDetails?.name,
        address1: event?.billingDetails?.address?.line1,
        address2: event?.billingDetails?.address?.line2 || '',
        city: event?.billingDetails?.address?.city,
        state: event?.billingDetails?.address?.state,
        postalCode: event?.billingDetails?.address?.postal_code,
        country: event?.billingDetails?.address?.country,
        phone: event?.billingDetails?.phone || '',
      }

      input.billing = addressDetails
    }

    if (onPaymentInitiated) {
      await onPaymentInitiated({ input })
    }

    const { error: submitError } = await elements.submit()

    if (submitError) {
      handleError(submitError)
      return
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: clientSecret || '',
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    })

    if (error) {
      handleError(error)
    } else {
      if (paymentIntent) {
        input.paymentMethod = {
          stripePaymentMethodId: paymentIntent.payment_method as string,
          stripePaymentIntentId: paymentIntent.id,
          paymentProcessorType: PaymentProcessorType.STRIPE,
        }

        // Update email if it wasn't set before
        if (paymentIntent?.receipt_email) {
          input.contact = {
            email: paymentIntent.receipt_email,
            emailOptin: true,
          }
        }
      }

      await onPaymentConfirmed({ input })
    }
  }

  return (
    <>
      {visibility !== 'hidden' && (
        <div className="mb-4 font-semibold">{t('express_checkout')}</div>
      )}

      <div
        id="express-checkout-element"
        className="mt-4 relative"
        style={{
          visibility,
        }}
      >
        <ExpressCheckoutElement
          onConfirm={onConfirm}
          onReady={onReady}
          options={{
            emailRequired: true,
          }}
        />
      </div>

      {stripeError && <StripeErrorAlert className="mt-4" error={stripeError} />}

      {visibility !== 'hidden' && <div className="divider">or</div>}
    </>
  )
}
